import React, { type PropsWithChildren, useRef } from 'react';
import { sharedDragController } from 'javascripts/dnd/controllers/DragController';
import { ApplicationView as view } from '@aha-app/mvc';
import { useDrop } from 'react-dnd';

type VoidDropTargetProps = {
  accepts: string[];
  className?: string;
  // If true, it will remove drag items if dropped on the target, otherwise it will just cancel the drag.
  removeItemsOnDrop?: boolean;
};

/**
 * The VoidDropTarget is used to represent a drop target that does not accept any dropping. This is useful to create
 * a void space around other droppable items. While dragging items, it will cause the preview items to be removed from
 * the active drag list.
 */
const VoidDropTarget: React.FC<PropsWithChildren<VoidDropTargetProps>> = ({
  accepts,
  children,
  className,
  removeItemsOnDrop = true,
}) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: accepts,
    hover(item, monitor) {
      if (monitor.isOver({ shallow: true })) {
        sharedDragController.actionSetVoidDropTarget();
      }
    },
    drop(draggedItem, monitor) {
      if (!monitor.didDrop()) {
        if (removeItemsOnDrop) {
          sharedDragController.actionFinishDrop();
        } else {
          sharedDragController.actionCancelDrag();
        }
      }
    },
  });

  drop(ref);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

export default view(VoidDropTarget);
